'use client'

import styles from './Sidebar.module.scss'
import useSWR from 'swr'
import Link from 'next/link'
import Icon, { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import { useEffect } from 'react'
import { throttle } from 'throttle-debounce'
import usePrint from '../hooks/usePrint'
import useAuth from '@crystal-eyes/hooks/useAuth'
import useEditProfile from '../hooks/useEditProfile'
import useTagging from '../hooks/useTagging'
import useVerifiedStatus from '@crystal-eyes/hooks/profiles/context/useVerifiedStatus'
import useIsOwnProfile from '@crystal-eyes/hooks/profiles/context/useIsOwnProfile'
import useMeV2 from '@crystal-eyes/hooks/user/useMeV2'
import useCanDeleteProfile from '@crystal-eyes/hooks/profiles/context/useCanDeleteProfile'
import logger from '@crystal-eyes/utils/logger'
import { identify, track } from '@dashboard/lib/analytics_v2'
import useProfileId from '@crystal-eyes/hooks/profiles/useProfileId'
import usePersonalInfo from '@crystal-eyes/hooks/profiles/usePersonalInfo'

interface Identity {
  id: string
  isCoworker?: boolean
  isCandidate?: boolean
}

const CONTENT_SELECTOR = '#content'
const WATCH_DOM_ELEMENTS = ['H1', 'H2', 'SECTION']
const SECTION_HIGHLIGHT_OFFSET = 100

type Heading = { id: string; title: string }
export type Section = {
  heading: Heading
  subHeadings: Heading[]
  icon?: Icons
}

type Props = {
  identity?: Identity
  fallback?: Section[]
}

const restrictedSubHeaderIds = ['profile_completeness']

export default function Sidebar({ identity, fallback }: Props) {
  const { authed, data: authDetails } = useAuth()
  const { data: me } = useMeV2()
  const { data: profileId } = useProfileId({ identity: identity! })
  const { data: personality } = usePersonality({ identity })
  const { data: personalInfo } = usePersonalInfo({ identity })
  const { data: verifiedStatus } = useVerifiedStatus({ identity })
  const { data: isOwnProfile } = useIsOwnProfile({ identity })
  const { data: canDeleteProfile, deleteProfile } = useCanDeleteProfile({
    identity,
  })

  useEffect(() => {
    // analytics
    if (authed && me && profileId) {
      if (identity?.isCandidate) {
        track('Candidate Profile Viewed', {
          candidate_id: profileId,
          candidate_disc: personality?.discType || '',
        })
      } else if (identity?.isCoworker) {
        track('Team Profile Viewed', {
          profile_id: profileId,
          profile_name: personalInfo?.fullName || '',
        })
      } else {
        track('Customer Profile Viewed', { custom_id: profileId })
      }
      const primaryEmail = me?.emails?.[0]?.value
      if (authDetails?.jwtPayload?.sub && primaryEmail)
        identify(
          authDetails?.jwtPayload?.sub.split(':')[1],
          {
            date_of_last_dashboard_login: Date.now(),
            hubspot_organization_id: '',
            firstName: authDetails?.jwtPayload?.name?.split(' ')[0],
            lastName: authDetails?.jwtPayload?.name?.split(' ')[1],
            email: primaryEmail,
          },
          { groupId: '' },
        )
    }
    if (!isOwnProfile) {
      track('Dashboard Profile Viewed')
    }
  }, [identity, authed, profileId, personality, authDetails])

  const {
    modalState: { setShow: setShowPrintModal },
  } = usePrint()

  const {
    editProfileState: { setState: setEditProfile },
  } = useEditProfile({ identity })

  const {
    taggingState: { setState: setTagging },
  } = useTagging({ identity })

  const { data: sections = [], mutate: refetchHeadings } = useSWR(
    'profile-page-sections',
    sectionsFetcher,
    {
      keepPreviousData: true,
      revalidateIfStale: true,
      dedupingInterval: 10000000,
      fallbackData: fallback,
    },
  )

  const { data: activeHeadings = [], mutate: refetchActiveHeadings } = useSWR(
    'profile-page-active-headings',
    activeSectionsFetcher,
    {
      keepPreviousData: true,
      revalidateIfStale: true,
      dedupingInterval: -1,
    },
  )

  useEffect(() => {
    refetchHeadings()
    const refetchTimeout = setTimeout(() => {
      refetchHeadings()
    }, 500)

    const content = document.querySelector(CONTENT_SELECTOR)!

    const { stop: stopWatchingHeaders } = observeHeadingChanges(
      content,
      refetchHeadings,
    )

    const { stop: stopWatchingScroll } = observeScroll(
      content,
      refetchActiveHeadings,
    )

    return () => {
      stopWatchingHeaders()
      stopWatchingScroll()
      if (refetchTimeout) clearTimeout(refetchTimeout)
    }
  }, [])

  const allowEdits =
    authed &&
    (isOwnProfile ||
      (!verifiedStatus?.isCrystalUser && !verifiedStatus?.isCrystalManaged))

  return (
    <nav id="sidebar" className={styles.sidebar}>
      <div className="contents sticky">
        {sections.map(section => (
          <section
            data-section={section.heading.id}
            key={section.heading.id}
            className={`${
              activeHeadings.includes(section.heading.id)
                ? 'active'
                : 'inactive'
            }`}
          >
            <div
              className={`heading ${
                activeHeadings.includes(section.heading.id) ? 'active' : ''
              }`}
            >
              {section.icon && (
                <Icon
                  icon={section.icon}
                  className={`CE disc-color ${personality?.discType || ''} ${
                    styles.icon
                  } ${
                    section.heading.id === 'personality'
                      ? styles.personality
                      : ''
                  }`}
                />
              )}

              <a href={`#${section.heading.id}`}>{section.heading.title}</a>
            </div>

            {section.subHeadings.map(subheading => (
              <div
                className={`subheading ${
                  activeHeadings.includes(subheading.id) ? 'active' : ''
                }`}
                key={subheading.id}
              >
                <a href={`#${subheading.id}`}>{subheading.title}</a>
              </div>
            ))}
          </section>
        ))}

        {authed && (
          <div className="utilities">
            <Link
              href="#"
              onClick={evt => {
                evt.preventDefault()
                setShowPrintModal(true)
              }}
            >
              <Icon icon={Icons.Download} className={styles.icon} /> Download
            </Link>

            {canDeleteProfile && (
              <Link
                href="#"
                onClick={evt => {
                  evt.preventDefault()
                  if (
                    confirm(
                      'Are you sure you want to permanently delete this profile?',
                    )
                  ) {
                    deleteProfile()
                      .then(() => (window.location.href = '/app'))
                      .catch(err => {
                        logger.error('Delete Profile Error', err)

                        alert('Something went wrong deleting the profile!')
                      })
                  }
                }}
              >
                <Icon icon={Icons.Trash} className={styles.icon} /> Delete
              </Link>
            )}

            {allowEdits && (
              <Link
                href="#"
                onClick={evt => {
                  evt.preventDefault()
                  setEditProfile('edit-profile')
                }}
              >
                <Icon icon={Icons.Edit} className={styles.icon} /> Edit
              </Link>
            )}

            <Link
              href="#"
              onClick={evt => {
                evt.preventDefault()
                setTagging('tagging-profile')
              }}
            >
              <Icon icon={Icons.Tag} className={styles.icon} /> Tags
            </Link>
          </div>
        )}
      </div>
    </nav>
  )
}

const sectionsFetcher = async () => {
  if (typeof document === 'undefined') return []

  const content = document.querySelector(CONTENT_SELECTOR)

  const sections: Section[] = []
  content?.querySelectorAll('section').forEach(section => {
    const headingElem = section.querySelector('h1')!
    const icon = headingElem.dataset['icon'] as Icons
    const headingSidebarTitle = headingElem.dataset['sidebarTitle'] as Icons

    const heading: Heading = {
      id: headingElem.id,
      title: headingSidebarTitle || headingElem.textContent || '',
    }

    const subHeadings: Heading[] = []
    const subheadingElems = section.querySelectorAll('h2')
    subheadingElems.forEach(subheading => {
      if (restrictedSubHeaderIds.includes(subheading.id)) return
      const subheadingSidebarTitle = subheading.dataset['sidebarTitle'] as Icons

      subHeadings.push({
        id: subheading.id,
        title: subheadingSidebarTitle || subheading.textContent || '',
      })
    })

    if (subHeadings.length > 0) {
      sections.push({
        heading,
        subHeadings,
        icon,
      })
    }
  })

  return sections
}

const activeSectionsFetcher = (): string[] => {
  const content = document.querySelector(CONTENT_SELECTOR)!

  let firstHeading: Element | undefined
  let activeHeading: Element | undefined
  content.querySelectorAll('h1').forEach((heading: Element) => {
    if (!firstHeading) firstHeading = heading
    if (heading.getBoundingClientRect().top < SECTION_HIGHLIGHT_OFFSET) {
      activeHeading = heading
    }
  })

  let activeSubheading: Element | undefined
  let lastSubheadingBeforeFold: Element | undefined
  content.querySelectorAll('h2').forEach((subHeading: Element) => {
    const isRestricted = restrictedSubHeaderIds.includes(subHeading.id)
    const boundingRect = subHeading.getBoundingClientRect()

    if (boundingRect.top < SECTION_HIGHLIGHT_OFFSET && !isRestricted) {
      lastSubheadingBeforeFold = subHeading
    }

    if (
      !activeSubheading &&
      boundingRect.top > 0 &&
      boundingRect.top < SECTION_HIGHLIGHT_OFFSET &&
      !isRestricted
    ) {
      activeSubheading = subHeading
    }
  })

  const activeElems: string[] = []
  if (activeHeading) activeElems.push(activeHeading.id)
  if (!activeHeading && firstHeading) activeElems.push(firstHeading.id)

  if (activeSubheading) activeElems.push(activeSubheading.id)
  if (!activeSubheading && lastSubheadingBeforeFold)
    activeElems.push(lastSubheadingBeforeFold.id)

  return activeElems
}

const observeHeadingChanges = (
  content: Element,
  refetchHeadings: any,
): { stop: () => void } => {
  const observer = new MutationObserver(mutations => {
    let shouldRefetchHeadings = false
    mutations.forEach(mutation => {
      if (mutation.type !== 'childList') return

      if (WATCH_DOM_ELEMENTS.includes(mutation.target?.nodeName)) {
        shouldRefetchHeadings = true
      }

      mutation.addedNodes.forEach(addedNode => {
        if (WATCH_DOM_ELEMENTS.includes(addedNode.nodeName))
          shouldRefetchHeadings = true
      })

      mutation.removedNodes.forEach(removedNode => {
        if (WATCH_DOM_ELEMENTS.includes(removedNode.nodeName))
          shouldRefetchHeadings = true
      })
    })

    if (shouldRefetchHeadings) refetchHeadings()
  })

  observer.observe(content!, {
    childList: true,
    subtree: true,
  })

  return {
    stop: () => {
      observer.disconnect()
    },
  }
}

const observeScroll = (
  _content: Element,
  refetchActiveHeadings: any,
): { stop: () => void } => {
  const handleScroll = throttle(200, () => {
    refetchActiveHeadings()
  })

  window.addEventListener('scroll', handleScroll)
  return {
    stop: () => {
      window.addEventListener('scroll', handleScroll)
    },
  }
}
